import type { VisualCriteriaPromptVar } from './VisualCriteriaForm'

// Shared coercion of a visual/dashboard criterion's seed value into what its
// prompt <input> can display. Extracted (behaviour-preserving) from the visual
// preview modal so the visual preview, dashboard preview, and the standalone
// runtime criteria bars all seed inputs identically.
//
//  - a date input needs YYYY-MM-DD (an ISO datetime is sliced to its date part)
//  - an unresolved `:token` (no resolved value available) can't be shown → ''
//  - a LIST (IN values / BETWEEN [lo, hi] pair) coerces per entry and joins
//    with ',' — the wire format the criteria form renders (chips / from-to)
//    and the engine splits back into binds. Positions are preserved, so a
//    half-filled between keeps its empty side.
export function formatCriteriaValueForInput(value: unknown, dataType?: string): string {
  if (value == null) return ''
  if (Array.isArray(value)) {
    return value.map((v) => formatCriteriaValueForInput(v, dataType)).join(',')
  }
  const s = String(value)
  if (s === '') return ''
  if (s.startsWith(':')) return '' // unresolved token — nothing to show
  if (dataType === 'date') {
    // A comma-joined pair/list ("2026-01-01,2026-06-30") coerces per part.
    if (s.includes(',')) {
      return s.split(',').map((p) => {
        const m = p.trim().match(/^\d{4}-\d{2}-\d{2}/)
        return m ? m[0] : ''
      }).join(',')
    }
    const m = s.match(/^\d{4}-\d{2}-\d{2}/)
    return m ? m[0] : ''
  }
  return s
}

/** Should a dashboard's criteria bar open EXPANDED? Only while a required
 *  criterion has no value. That is the server's call, not a flag: a tile comes
 *  back `needsInput` exactly when a prompt it requires has no value — none
 *  entered, none passed and no default. Its missing names are the VISUAL's
 *  prompt names, which a tile binding may map from a differently named
 *  dashboard criterion, so they are not matched: any such tile opens the bar,
 *  provided the dashboard has criteria to fill in. */
export function dashboardCriteriaNeedInput(
  tiles: ReadonlyArray<{ needsInput?: boolean }> | undefined,
  criteria: ReadonlyArray<unknown> | undefined,
): boolean {
  if (!criteria?.length) return false
  return (tiles || []).some((t) => !!t?.needsInput)
}

/** One finding from the criteria's checks (mandatory / validations), as the
 *  server returns it in `criteriaErrors` / `criteriaNotices`. */
export interface CriteriaIssue {
  param: string
  message: string
  code?: string
  severity?: 'E' | 'W' | 'P' | string
}

/** The required criteria still blank — what the bar checks before it runs.
 *  A range needs both ends. Hidden criteria run on their default, so they are
 *  never asked for. */
export function missingRequired(
  promptVars: VisualCriteriaPromptVar[], values: Record<string, string>,
): VisualCriteriaPromptVar[] {
  return promptVars.filter((pv) => {
    if (!pv.required || pv.hidden) return false
    const v = (values[pv.name] ?? '').trim()
    if ((pv.operator || '').trim().toLowerCase() === 'between') {
      const [lo = '', hi = ''] = v.split(',')
      return !lo.trim() || !hi.trim()
    }
    return !v
  })
}

/** The host's own required check, as findings — run before a run is asked
 *  for, so a blank required criterion is said at once (the server would refuse
 *  it the same way). */
export function requiredIssues(
  promptVars: VisualCriteriaPromptVar[], values: Record<string, string>,
): CriteriaIssue[] {
  return missingRequired(promptVars, values).map((pv) => ({
    param: pv.name, message: `${pv.label} is required`, code: 'required', severity: 'E',
  }))
}

/** The issues that name `param` (names meet case-insensitively). */
export function issuesFor(issues: CriteriaIssue[] | undefined, param: string): CriteriaIssue[] {
  const want = param.trim().toUpperCase()
  return (issues || []).filter((i) => (i.param || '').trim().toUpperCase() === want)
}
